import type { Availability } from './availability'
import type { RoleAccessContext } from './roleAccess'
import type { AccessRule } from './routeRegistry'
import type { AuthzContextState } from './useAuthzContextQuery'

/**
 * `pending` and `unavailable` are withheld answers rather than denials: a caller who has access must
 * not be redirected because a check had not finished or could not be made.
 */
export type AccessAnswer = 'allowed' | 'denied' | 'pending' | 'unavailable'

const accessAnswer = (isAllowed: boolean): AccessAnswer => (isAllowed ? 'allowed' : 'denied')

/**
 * The route gate's only interpreter of the authorization context's four states. A route declares which
 * answer source it trusts through its rule's `kind`, and never sees `loading` or `unavailable` itself.
 */
export const resolveAccess = (
  rule: AccessRule,
  availability: Availability,
  authzContext: AuthzContextState,
  roleContext: RoleAccessContext
): AccessAnswer => {
  // Availability is a fact about the organisation and the deployment, so it short-circuits: a grant
  // must not reveal a module the organisation does not have.
  if (availability === 'pending') return 'pending'
  if (availability === 'unavailable') return 'denied'

  // Neither of these consults the Permissions Service, so neither waits on it.
  if (rule.kind === 'open') return 'allowed'
  if (rule.kind === 'role') return accessAnswer(rule.role(roleContext))

  switch (authzContext.status) {
    case 'loading':
      return 'pending'
    case 'unavailable':
      return 'unavailable'
    case 'legacy':
      // `fga` means the page does not exist for an organisation without the service.
      return rule.kind === 'fga' ? 'denied' : accessAnswer(rule.role(roleContext))
    case 'resolved':
      return accessAnswer(rule.check(authzContext))
  }
}
